import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconArrowLeft,
  IconPencil,
  IconSend,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import { useContentPlansStore } from "./contentPlansStore";
import ResearchPanel from "./ResearchPanel";
import ContentItem from "./ContentItem";
import ClusterQuota from "./ClusterQuota";
import {
  clusterFromWorkstream,
  clusterResearchStatus,
  editionLabel,
  usableEditions,
} from "./clusterDisplay";

export default function ClusterPage() {
  const { planId, workstreamId } = useParams();
  const [, setLocation] = useLocation();
  const {
    currentPlan,
    isLoading,
    fetchContentPlan,
    patchWorkstream,
    deleteWorkstream,
    generateWorkstream,
  } = useContentPlansStore();
  const search = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const initialCountry = search.get("country") || undefined;
  const initialLanguage = search.get("language") || undefined;

  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ title: "", goal: "" });
  const [writing, setWriting] = useState(false);

  const refreshPlan = useCallback(() => {
    if (planId) fetchContentPlan(planId);
  }, [planId, fetchContentPlan]);

  useEffect(() => {
    refreshPlan();
  }, [refreshPlan]);

  const workstream = currentPlan?.workstreams?.find((w) => w.id === workstreamId);

  const cluster = useMemo(
    () =>
      workstream
        ? clusterFromWorkstream(workstream, planId, currentPlan?.name)
        : null,
    [workstream, planId, currentPlan?.name]
  );

  const researchStatus = cluster ? clusterResearchStatus(cluster) : "none";
  const showArticles = researchStatus === "ready";

  const ideas = [...(workstream?.contentIdeas || [])].sort((a, b) => {
    const aIsPillar = a.contentRole?.toLowerCase() === "pillar";
    const bIsPillar = b.contentRole?.toLowerCase() === "pillar";
    if (aIsPillar && !bIsPillar) return -1;
    if (!aIsPillar && bIsPillar) return 1;
    return 0;
  });

  // Ideation is a cheap job phase after the dossier is Ready. Poll until titles land.
  useEffect(() => {
    if (!showArticles || ideas.length > 0 || !planId) return undefined;
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      fetchContentPlan(planId);
      if (n >= 8) clearInterval(id);
    }, 5000);
    return () => clearInterval(id);
  }, [showArticles, ideas.length, planId, fetchContentPlan]);

  if (isLoading && !currentPlan) {
    return (
      <Container size="lg" py="xl">
        <LoadingOverlay visible />
      </Container>
    );
  }

  if (!workstream || !cluster) {
    return (
      <Container size="lg" py="xl">
        <Text>Cluster not found</Text>
        <Button
          mt="md"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => setLocation("/plans")}
        >
          All clusters
        </Button>
      </Container>
    );
  }

  const editions = usableEditions(cluster.editions);
  const hasDrafts = ideas.some(
    (i) => i.contentDocumentId && i.status !== "published"
  );
  const writableIdeas = ideas.filter(
    (i) =>
      (i.status === "idea" || i.status === "approvedForGeneration") &&
      !i.contentDocumentId
  );

  const save = async () => {
    await patchWorkstream(planId, workstream.id, editData);
    setEditing(false);
  };

  const confirmWrite = () => {
    const n = writableIdeas.length;
    modals.openConfirmModal({
      title: n === 1 ? "Write this article" : `Write these ${n} articles`,
      children: (
        <Text size="sm">
          Start writing now from this cluster&apos;s research. A brief is not
          required. Skip any article you do not want first.
        </Text>
      ),
      labels: {
        confirm: n === 1 ? "Write it" : `Write ${n} articles`,
        cancel: "Cancel",
      },
      confirmProps: { color: "blue" },
      onConfirm: async () => {
        setWriting(true);
        try {
          await generateWorkstream(planId, workstream.id);
        } finally {
          setWriting(false);
        }
      },
    });
  };

  const confirmDelete = () => {
    modals.openConfirmModal({
      title: "Delete this cluster",
      children: (
        <Text size="sm">
          {researchStatus === "ready"
            ? `Delete "${workstream.title}"? This does not refund a researched cluster.`
            : `Delete "${workstream.title}"?`}
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        const ok = await deleteWorkstream(planId, workstream.id);
        if (ok) setLocation(`/plans/${planId}`);
      },
    });
  };

  const statusBadge =
    researchStatus === "ready" && editions.length > 0 ? (
      editions.map((e) => (
        <Badge key={e.id || editionLabel(e)} color="grape" variant="light">
          {editionLabel(e)}
        </Badge>
      ))
    ) : researchStatus === "ready" ? (
      <Badge color="teal" variant="light">
        Researched
      </Badge>
    ) : researchStatus === "running" ? (
      <Badge color="blue" variant="light">
        Researching
      </Badge>
    ) : (
      <Badge color="orange" variant="light">
        Not researched
      </Badge>
    );

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} href="/plans">
            Clusters
          </Anchor>
          <Anchor component={Link} href={`/plans/${planId}`}>
            {currentPlan?.name || "Plan"}
          </Anchor>
          <Text>{workstream.title}</Text>
        </Breadcrumbs>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            {editing ? (
              <>
                <TextInput
                  label="Title"
                  value={editData.title}
                  onChange={(e) =>
                    setEditData({ ...editData, title: e.target.value })
                  }
                />
                <Textarea
                  label="Goal"
                  value={editData.goal}
                  minRows={3}
                  onChange={(e) =>
                    setEditData({ ...editData, goal: e.target.value })
                  }
                />
                <Group>
                  <Button onClick={save}>Save</Button>
                  <Button variant="default" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <div style={{ minWidth: 0 }}>
                    <Title order={2}>{workstream.title}</Title>
                    {workstream.goal && (
                      <Text c="dimmed" mt="xs">
                        {workstream.goal}
                      </Text>
                    )}
                  </div>
                  <Group gap="xs" style={{ flexShrink: 0 }}>
                    {statusBadge}
                  </Group>
                </Group>
                <Group gap="xs">
                  <Button
                    variant="default"
                    size="xs"
                    leftSection={<IconPencil size={14} />}
                    onClick={() => {
                      setEditData({
                        title: workstream.title || "",
                        goal: workstream.goal || "",
                      });
                      setEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="default"
                    size="xs"
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={confirmDelete}
                  >
                    Delete
                  </Button>
                </Group>
                <ClusterQuota />
              </>
            )}
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          {researchStatus !== "none" && (
            <Title order={3} mb="md">
              Research
            </Title>
          )}
          {currentPlan?.brandId ? (
            <ResearchPanel
              brandId={currentPlan.brandId}
              planId={planId}
              workstreamId={workstream.id}
              onClusterUpdated={refreshPlan}
              initialCountry={initialCountry}
              initialLanguage={initialLanguage}
            />
          ) : (
            <Text c="dimmed" size="sm">
              Brand is missing on this plan, so research cannot start.
            </Text>
          )}
        </Card>

        {showArticles ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={3}>Articles</Title>
              <Group gap="xs">
                {writableIdeas.length > 0 && (
                  <Button
                    size="xs"
                    leftSection={<IconSparkles size={14} />}
                    onClick={confirmWrite}
                    loading={writing}
                  >
                    {writableIdeas.length === 1
                      ? "Write this article"
                      : `Write these ${writableIdeas.length} articles`}
                  </Button>
                )}
                {hasDrafts && (
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconSend size={14} />}
                    component={Link}
                    href={`/plans/${planId}/clusters/${workstream.id}/review`}
                  >
                    Review drafts
                  </Button>
                )}
              </Group>
            </Group>
            {ideas.length === 0 ? (
              <Text c="dimmed" size="sm">
                Articles are proposed from this research. They appear here when
                ready.
              </Text>
            ) : (
              <Stack gap="sm">
                {ideas.map((contentItem) => (
                  <ContentItem
                    key={contentItem.id}
                    contentItem={contentItem}
                    planId={planId}
                    workstreamId={workstream.id}
                    workstreamTitle={workstream.title}
                    hasResearchedDossier={workstream.hasResearchedDossier}
                  />
                ))}
              </Stack>
            )}
          </Card>
        ) : (
          researchStatus !== "running" && (
            <Text c="dimmed" size="sm">
              Articles appear after this research.
            </Text>
          )
        )}
      </Stack>
    </Container>
  );
}
